import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { Observable, of, forkJoin } from 'rxjs';
import { map, tap, shareReplay } from 'rxjs/operators';
import { MaterialModule } from '../../../shared/material.module';
import { UserService } from '../../../services/user.service';
import { AppUserBase } from '../../../models/appUserBase';
import { LargeShareholderCategory } from '../../../models/largeShareholderCategory';
import { LutService } from '../../../services/lut.service';
import { CompanyVoteProposalNameSearchFilter } from '../../../models/companyVoteProposalNameSearchFilter';
import { CountryFlat } from '../../../models/country';
import { Helpers } from '../../../utilities/helpers';
import { REGIONS, RegionOption } from '../../../utilities/regions';
import { TextNormalizeDirective } from '../../../directives/text-normalize.directive';

@Component({
  selector: 'app-company-vote-proposal-name-search-box',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialModule,
    MatIconModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    FormsModule,
    TextNormalizeDirective
  ],
  templateUrl: './company-vote-proposal-name-search-box.component.html',
  styleUrl: './company-vote-proposal-name-search-box.component.scss'
})
export class CompanyVoteProposalNameSearchBoxComponent implements OnInit, OnChanges {
  @Input() isSearchVisible: boolean = false;
  @Input() initialFilter: CompanyVoteProposalNameSearchFilter = {};

  regions: RegionOption[] = REGIONS;
  years: string[] = [];

  @Output() searchFilter = new EventEmitter<CompanyVoteProposalNameSearchFilter>();
  @Output() showCompaniesChange = new EventEmitter<boolean>();

  showCompanies = false;

  categories$!: Observable<LargeShareholderCategory[] | null>;
  subCategories$: Observable<LargeShareholderCategory[]> = of([]);
  allCategories: LargeShareholderCategory[] = [];
  countries$!: Observable<CountryFlat[]>;
  allCountries: CountryFlat[] = [];
  users$!: Observable<AppUserBase[]>;

  searchForm: CompanyVoteProposalNameSearchFilter = {
    fullName: undefined,
    categoryId: undefined,
    subCategoryId: undefined,
    countryId: undefined,
    region: undefined,
    year: Helpers.defaultMeasurementYear(),
    createdBy: undefined
  };

  constructor(private lutService: LutService, private userService: UserService) {
    this.years = Helpers.measurements();
  }

  ngOnInit(): void {
    const categoriesData$ = this.lutService.getLargeShareholderCategories().pipe(
      tap(categories => {
        this.allCategories = categories;
      }),
      shareReplay(1)
    );

    const countriesData$ = this.lutService.getVisibleCountries().pipe(
      tap(countries => {
        this.allCountries = countries;
      }),
      shareReplay(1)
    );

    forkJoin({
      categories: categoriesData$,
      countries: countriesData$
    }).subscribe(() => {
      this.populateSearchForm();
    });

    this.categories$ = categoriesData$.pipe(
      map(categories => categories
        .filter(category => category.parentId === null)
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
      )
    );

    this.countries$ = countriesData$.pipe(
      map(countries => countries.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })))
    );

    this.users$ = this.userService.getUsers(1, 100, 'FullName', true).pipe(
      map(response => response.data)
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialFilter'] && !changes['initialFilter'].firstChange) {
      this.populateSearchForm();
    }
  }

  private populateSearchForm(): void {
    if (this.initialFilter && Object.keys(this.initialFilter).length > 0) {
      this.searchForm = {
        fullName: this.initialFilter.fullName,
        categoryId: this.initialFilter.categoryId,
        subCategoryId: this.initialFilter.subCategoryId,
        countryId: this.initialFilter.countryId,
        region: this.initialFilter.region,
        year: this.initialFilter.year,
        createdBy: this.initialFilter.createdBy
      };

      if (this.initialFilter.categoryId) {
        this.subCategories$ = of(
          this.allCategories
            .filter(category => category.parentId === this.initialFilter.categoryId)
            .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
        );
      }

      if (this.initialFilter.region) {
        this.countries$ = of(
          this.allCountries
            .filter(country => country.region === this.initialFilter.region)
            .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
        );
      }
    }
  }

  onRegionChange(): void {
    if (this.searchForm.region) {
      this.countries$ = of(
        this.allCountries
          .filter(country => country.region === this.searchForm.region)
          .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
      );
    } else {
      this.countries$ = of(
        this.allCountries.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
      );
      this.searchForm.countryId = undefined;
    }
  }

  onCategoryChange(): void {
    if (this.searchForm.categoryId) {
      this.subCategories$ = of(
        this.allCategories
          .filter(category => category.parentId === this.searchForm.categoryId)
          .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
      );
    } else {
      this.subCategories$ = of([]);
      this.searchForm.subCategoryId = undefined;
    }
  }

  onSearch(): void {
    this.searchFilter.emit({ ...this.searchForm });
  }

  onShowCompaniesChange(): void {
    this.showCompaniesChange.emit(this.showCompanies);
  }

  clearSearch(): void {
    this.searchForm = {
      fullName: undefined,
      categoryId: undefined,
      subCategoryId: undefined,
      countryId: undefined,
      region: undefined,
      year: Helpers.defaultMeasurementYear(),
      createdBy: undefined
    };
    this.subCategories$ = of([]);
    this.countries$ = of(
      this.allCountries.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
    );
    this.searchFilter.emit({ ...this.searchForm });
  }
}
